import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/services/supabase"

export function useAuth() {
    return useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            try {
                const { data: { user }, error: authError } = await supabase.auth.getUser()
                if (authError || !user) return null

                const { data: profile } = await supabase
                    .from('profiles')
                    .select('store_name, subscription_status, trial_ends_at, current_period_end, stripe_customer_id')
                    .eq('id', user.id)
                    .maybeSingle()

                const isProfileComplete = !!profile?.store_name
                const status = profile?.subscription_status || 'trialing'
                const trialEndsAt = profile?.trial_ends_at ? new Date(profile.trial_ends_at) : null
                const isTrialExpired = trialEndsAt ? trialEndsAt < new Date() : false
                const currentPeriodEnd = profile?.current_period_end ? new Date(profile.current_period_end) : null
                const isSubscriptionExpired = currentPeriodEnd ? currentPeriodEnd < new Date() : false

                // User has access if:
                // 1. Subscription is active OR trialing (not expired) OR past due (grace period)
                // 2. AND they have a stripe_customer_id (meaning they've been through checkout)
                const subscriptionActive = !!profile?.stripe_customer_id && (
                    status === 'active' ||
                    (status === 'trialing' && !isTrialExpired) ||
                    (status === 'past_due' && !isSubscriptionExpired)
                )

                return {
                    ...user,
                    profile,
                    isProfileComplete,
                    subscriptionActive,
                    subscriptionStatus: status,
                    currentPeriodEnd: profile?.current_period_end || null
                }
            } catch (error) {
                console.error("Auth query error:", error)
                return null
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: false,
    })
}
